import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort, SortDirection } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { addDays } from '@fullcalendar/core/internal';
import { ConfigService } from 'app/services';
import { PartnerFinanceService } from 'app/services/partner/partner-finance.service';
import { GetFinanceReportsRequest } from 'app/models/partner/get-finance-reports-request';
import {
	GetFinanceReportsResponse,
	FinanceReportDto,
} from 'app/models/partner/get-finance-reports-response';
import { MessageService } from 'primeng/api';
import { Utils } from 'app/utils/utils';
import { ExportFileExtension, ExportFileTypeEnum } from 'app/models';
import moment from 'moment';

export interface FinanceReportData {
	timeDelivered: string;
	orderId: string;
	orderValue: number;
	merchantFundedPromo: number;
	commission: number;
	withholdingTax: number;
	deliveryFeeDiscount: number;
	earnings: number;
}

@Component({
	selector: 'partner-finance-reports',
	templateUrl: './partner-finance-reports.component.html',
	styleUrls: [
		'./partner-finance-reports.component.scss',
		'../../../../../styles/m3-defaul-material-theme.scss',
	],
})
export class PartnerFinanceReportsComponent implements OnInit, AfterViewInit {
	constructor(
		public configService: ConfigService,
		private fb: FormBuilder,
		private partnerFinanceService: PartnerFinanceService,
		private messageService: MessageService
	) {}

	fileTypeOptions = ExportFileTypeEnum; // Default selected file type

	displayedColumns: string[] = [
		'timeDelivered',
		'orderId',
		'orderValue',
		'merchantFundedPromo',
		'commission',
		'withholdingTax',
		'deliveryFeeDiscount',
		'earnings',
	];
	dataSource = new MatTableDataSource<FinanceReportData>([]);
	range!: FormGroup;
	loading = false;
	isEmpty = false;

	// Server-side pagination properties
	totalRecords = 0;
	pageSize = 10;
	pageIndex = 0;
	pageSizeOptions = [5, 10, 20, 50];
	currentSortField = 'timeDelivered'; // Default sort by time delivered
	currentSortOrder = 1; // Default to descending (newest first)
	currentSortDirection: SortDirection = 'desc';

	@ViewChild(MatPaginator) paginator!: MatPaginator;
	@ViewChild(MatSort) sort!: MatSort;

	ngOnInit() {
		this.setDefaultRangePicker();
	}

	ngAfterViewInit() {
		// Don't set paginator and sort on dataSource for server-side pagination
		// this.dataSource.paginator = this.paginator;
		// this.dataSource.sort = this.sort;
	}

	setDefaultRangePicker() {
		const today = new Date();
		const last7Days = addDays(today, -6); // includes today, so 7 total days

		this.range = this.fb.group({
			start: [last7Days],
			end: [today], // Set end date to null initially
		});

		this.loadFinanceReports();
	}

	loadFinanceReports() {
		if (!this.range) {
			return;
		}

		this.loading = true;
		const request: GetFinanceReportsRequest = {
			TimeZoneOffset: Utils.getTimezoneOffset(),
			LocationId: undefined, // You can add location filter if needed
			FromDate: this.range.value.start, // Always have default value
			ToDate: this.range.value.end, // Always have default value
			// BaseAdminRequest properties
			SearchName: '',
			Skip: this.pageIndex * this.pageSize,
			Take: this.pageSize,
			SortField: this.currentSortField,
			SortOrder: this.currentSortOrder,
			HeaderColumns: [],
			DataColumns: [],
		};

		this.partnerFinanceService
			.getFinanceReports(request, this.messageService)
			.then((response: GetFinanceReportsResponse) => {
				if (response.Success && response.Items && response.Items.length > 0) {
					const mappedData = response.Items.map((item: FinanceReportDto) => ({
						timeDelivered: this.formatDateTime(item.TimeDelivered),
						orderId: item.OrderId,
						orderValue: item.OrderValue,
						merchantFundedPromo: item.MerchantFundedPromo,
						commission: item.Commission,
						withholdingTax: item.WithholdingTax,
						deliveryFeeDiscount: item.DeliveryFeeDiscount,
						earnings: item.Earnings,
					}));
					this.dataSource.data = mappedData;
					this.totalRecords = response.TotalRecords || 0;
					this.isEmpty = false;
				} else {
					this.dataSource.data = [];
					this.totalRecords = 0;
					this.isEmpty = true;
				}
			})
			.catch((error) => {
				this.messageService.add({
					severity: 'error',
					summary: 'Error',
					detail: 'Failed to load finance reports',
				});
				this.dataSource.data = [];
				this.totalRecords = 0;
				this.isEmpty = true;
			})
			.finally(() => {
				this.loading = false;
			});
	}

	onDateRangeChange() {
		// Check if both individual controls are valid and have values
		const startControl = this.range.get('start');
		const endControl = this.range.get('end');

		const hasStartValue =
			startControl?.value !== null && startControl?.value !== undefined;
		const hasEndValue =
			endControl?.value !== null && endControl?.value !== undefined;

		// Only proceed if both controls have values
		if (!hasStartValue || !hasEndValue) {
			return;
		} else {
			// Reset pagination when date range changes
			this.pageIndex = 0;
			this.loading = true;
			this.isEmpty = false;
			this.loadFinanceReports();
		}
	}

	private formatDateTime(dateTime: string): string {
		const date = new Date(dateTime);
		return date.toLocaleTimeString('en-US', {
			hour: 'numeric',
			minute: '2-digit',
			hour12: true,
		});
	}

	exportReport(fileType: ExportFileTypeEnum) {
		const request: GetFinanceReportsRequest = {
			TimeZoneOffset: Utils.getTimezoneOffset(),
			LocationId: undefined, // You can add location filter if needed
			FromDate: this.range.value.start,
			ToDate: this.range.value.end,
			FileType: fileType,
			// BaseAdminRequest properties for export
			SearchName: '',
			Skip: 0,
			Take: 0, // 0 means get all records for export
			SortField: this.currentSortField,
			SortOrder: this.currentSortOrder,
			HeaderColumns: [],
			DataColumns: [],
		};

		this.partnerFinanceService
			.exportFinanceReports(request, this.messageService)
			.then((data) => {
				this.messageService.add({
					severity: 'success',
					summary: 'Success',
					detail: 'Success',
				});
				Utils.saveFileResponse(
					data,
					'FinanceReport' +
						moment(new Date()).format('DD MMM YYYY hh:mm:ss A') +
						ExportFileExtension[
							ExportFileTypeEnum[fileType] as keyof typeof ExportFileExtension
						]
				);
				this.loading = false;
			})
			.catch((err) => {
				this.loading = false;
				throw err;
			});
	}

	// Pagination event handlers
	onPageChange(event: PageEvent) {
		this.pageIndex = event.pageIndex;
		this.pageSize = event.pageSize;
		this.loadFinanceReports();
	}

	// Sorting event handler
	onSortChange(event: Sort) {
		this.currentSortField = event.active;
		this.currentSortOrder = this.currentSortDirection === 'asc' ? 1 : -1;
		this.currentSortDirection = this.currentSortDirection === 'asc' ? 'desc' : 'asc';
		this.pageIndex = 0; // Reset to first page when sorting changes
		this.loadFinanceReports();
	}
}
